// pricing-lab — the price lab's client state (Booking step 2). Inputs live
// here so they survive every re-quote; the server re-renders only the
// result (#pricing-lab-result) and the config-derived inputs
// (#pricing-lab-dynamic, OOB), which call syncMeta() on arrival. Every
// posted value is a `sample_*` hidden input bound to this state; changes
// made by buttons call requote() (a programmatic change fires no native
// `change` event for htmx to see).
export interface LabGroup { from: number; to: number }
export interface LabMeta {
  groups: LabGroup[];
  guestCountAge: number;
  maxOccupancy: number | null;
  // The exemption qualifiers the quoted config's taxes honour — one
  // checkbox each in #pricing-lab-dynamic. Always sent by the server;
  // `undefined` (an older/hand-built payload) means "unknown", and leaves
  // the declarations alone.
  qualifiers?: string[];
  // Set by the server when the config-derived inputs couldn't be rendered
  // (e.g. a half-typed fee mid-edit). `groups` is then empty too, but this
  // flag is the authoritative signal — a future response could in principle
  // send an error with stale/placeholder groups, and it must still be
  // ignored rather than applied.
  error?: boolean;
}
export type Counts = Record<string, number>;
export interface LabInit { arrival: string; departure: string; bookingDate: string; maxOccupancy: number | null }

/**
 * REQUOTE_DEBOUNCE_MS collapses a burst of counter clicks into ONE request.
 * Without it every click fired a request that the next one immediately
 * aborted (hx-sync="this:replace"), which spammed the console. The htmx
 * `keyup changed delay:400ms` trigger is unrelated and stays.
 */
export const REQUOTE_DEBOUNCE_MS = 250;

export function childAges(counts: Counts): number[] {
  const out: number[] = [];
  Object.keys(counts || {}).map(Number).filter(Number.isFinite).sort((a, b) => a - b)
    .forEach((from) => { for (let i = 0; i < (counts[String(from)] || 0); i++) out.push(from); });
  return out;
}

export function countedGuests(adults: number, counts: Counts, guestCountAge: number): number {
  return adults + childAges(counts).filter((a) => a >= guestCountAge).length;
}

export function canAdd(adults: number, counts: Counts, meta: LabMeta, age: number | null): boolean {
  if (meta.maxOccupancy === null || meta.maxOccupancy <= 0) return true;
  if (age !== null && age < meta.guestCountAge) return true;
  return countedGuests(adults, counts, meta.guestCountAge) < meta.maxOccupancy;
}

export function pruneCounts(counts: Counts, groups: LabGroup[]): { counts: Counts; changed: boolean } {
  const keep = new Set((groups || []).map((g) => String(g.from)));
  const out: Counts = {};
  let changed = false;
  for (const k of Object.keys(counts || {})) {
    if (keep.has(k)) out[k] = counts[k];
    else changed = true;
  }
  return { counts: out, changed };
}

/**
 * pruneQualifiers drops declarations `allowed` no longer contains — the
 * qualifier equivalent of pruneCounts. Without it, removing a qualifier
 * from a tax would leave a ticked hidden input that still changes the
 * quote with no checkbox to untick.
 */
export function pruneQualifiers(list: string[], allowed: string[]): { qualifiers: string[]; changed: boolean } {
  const keep = new Set(allowed || []);
  const out = (list || []).filter((q) => keep.has(q));
  return { qualifiers: out, changed: out.length !== (list || []).length };
}

export function setExtraQty(extras: Record<string, number>, id: string, qty: number): Record<string, number> {
  const out = { ...extras };
  if (qty > 0) out[id] = qty;
  else delete out[id];
  return out;
}

export function toggleId(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

export function createPricingLab(init: LabInit): Record<string, any> {
  return {
    arrival: init.arrival,
    departure: init.departure,
    bookingDate: init.bookingDate,
    adults: 2,
    assumedRate: "",
    counts: {} as Counts,
    extras: {} as Record<string, number>,
    excluded: [] as string[],
    qualifiers: [] as string[],
    meta: { groups: [], guestCountAge: 0, maxOccupancy: init.maxOccupancy } as LabMeta,
    requoteTimer: 0 as unknown as ReturnType<typeof setTimeout>,
    childAgesList(): number[] { return childAges(this.counts); },
    extrasList(): { id: string; qty: number }[] {
      return Object.keys(this.extras).sort().map((id) => ({ id, qty: this.extras[id] }));
    },
    count(from: number): number { return this.counts[String(from)] || 0; },
    canAddAdult(): boolean { return canAdd(this.adults, this.counts, this.meta, null); },
    canAddChild(from: number): boolean { return canAdd(this.adults, this.counts, this.meta, from); },
    atCapacity(): boolean { return !canAdd(this.adults, this.counts, this.meta, null); },
    incAdult() { if (this.canAddAdult()) { this.adults++; this.requote(); } },
    decAdult() { if (this.adults > 1) { this.adults--; this.requote(); } },
    inc(from: number) { if (this.canAddChild(from)) { this.counts = { ...this.counts, [String(from)]: this.count(from) + 1 }; this.requote(); } },
    dec(from: number) { if (this.count(from) > 0) { this.counts = { ...this.counts, [String(from)]: this.count(from) - 1 }; this.requote(); } },
    setExtra(id: string, qty: number) { this.extras = setExtraQty(this.extras, id, qty); this.requote(); },
    toggleRule(id: string) { this.excluded = toggleId(this.excluded, id); this.requote(); },
    hasQualifier(q: string): boolean { return this.qualifiers.includes(q); },
    setQualifier(q: string, on: boolean) {
      if (on === this.hasQualifier(q)) return;
      this.qualifiers = on ? [...this.qualifiers, q] : this.qualifiers.filter((x: string) => x !== q);
      this.requote();
    },
    syncMeta(m: LabMeta) {
      // `error` is the authoritative "the server couldn't derive this"
      // signal — honour it even if some fields (or stale/placeholder
      // groups) came along with it. Otherwise, GuestAgeGroups always
      // yields >= 1 group, so an empty list only ever comes from a
      // response that couldn't derive them (an error too). Ignoring both
      // keeps the operator's child counters through a transient config
      // error (e.g. a half-typed fee). The handler also skips the OOB then.
      if (!m || m.error || !Array.isArray(m.groups) || m.groups.length === 0) return;
      this.meta = m;
      const r = pruneCounts(this.counts, m.groups);
      let changed = r.changed;
      if (r.changed) this.counts = r.counts;
      // A qualifier the taxes stopped honouring loses its checkbox in this
      // very swap, so its declaration has to go with it.
      if (Array.isArray(m.qualifiers)) {
        const q = pruneQualifiers(this.qualifiers, m.qualifiers);
        if (q.changed) { this.qualifiers = q.qualifiers; changed = true; }
      }
      if (changed) this.requote();
    },
    requote() {
      const self = this as any;
      const el = self.$root as HTMLElement | undefined;
      if (!el || !self.$nextTick) return;
      clearTimeout(self.requoteTimer);
      self.requoteTimer = setTimeout(() => {
        self.$nextTick(() => (window as any).htmx?.trigger(el, "pricing-lab-requote"));
      }, REQUOTE_DEBOUNCE_MS);
    },
  };
}
